// src/components/admin/ReviewTypeChangeModal.tsx

import React, { useState } from 'react';
import {
    X, Check, AlertCircle, Building2, Mail,
    XCircle
} from 'lucide-react';
import DocumentViewer from './DocumentViewer';
import { formatDate } from '../../utils/date';
import type { VendorTypeChangeRequest } from '../../types';

interface ReviewTypeChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (action: 'approve' | 'reject', reason?: string) => Promise<void>;
    request: VendorTypeChangeRequest;
    isProcessing?: boolean;
}
const REJECTION_REASONS = [
    {
        value: 'insufficient_documents',
        label: 'Insufficient Documentation',
        description: 'Missing or incomplete business registration documents or identification'
    },
    {
        value: 'business_type_mismatch',
        label: 'Business Type Mismatch',
        description: 'The requested business type does not match the provided documentation'
    },
    {
        value: 'policy_violation',
        label: 'Policy Violation',
        description: 'The requested change violates platform policies or terms of service'
    },
    {
        value: 'duplicate_request',
        label: 'Duplicate Request',
        description: 'A similar request has already been submitted or processed'
    },
    {
        value: 'invalid_information',
        label: 'Invalid Information',
        description: 'The provided information is incorrect or cannot be verified'
    },
    {
        value: 'other',
        label: 'Other',
        description: 'Please provide a detailed reason below'
    },
];

const ReviewTypeChangeModal: React.FC<ReviewTypeChangeModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    request,
    isProcessing = false,
}) => {
    const [action, setAction] = useState<'approve' | 'reject'>('approve');
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionReasonCustom, setRejectionReasonCustom] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);

    if (!isOpen) return null;

    const formatType = (type: string) => {
        const types: Record<string, string> = {
            individual: 'Individual',
            sole_proprietor: 'Sole Proprietor',
            llc: 'LLC',
            corporation: 'Corporation',
            partnership: 'Partnership',
            non_profit: 'Non-Profit',
        };
        return types[type] || type;
    };

    const handleRejectClick = () => {
        if (!rejectionReason) {
            setShowErrors(true);
            return;
        }
        if (rejectionReason === 'other' && !rejectionReasonCustom.trim()) {
            setShowErrors(true);
            return;
        }
        setShowRejectModal(true);
    };

    const handleApprove = async () => {
        await onConfirm('approve');
        setShowApproveModal(false);
    };

    const handleReject = async () => {
        const selectedReason = REJECTION_REASONS.find(r => r.value === rejectionReason);
        let reason = '';

        if (rejectionReason === 'other') {
            reason = rejectionReasonCustom;
        } else {
            reason = `${selectedReason?.label}: ${selectedReason?.description}`;
            if (rejectionReasonCustom.trim()) {
                reason += `\n\nAdditional details: ${rejectionReasonCustom}`;
            }
        }

        await onConfirm('reject', reason);
        setShowRejectModal(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Review Type Change Request</h3>
                            <p className="text-sm text-gray-500">{request.vendor_name}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    {/* Request Details */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Current Type</p>
                            <p className="font-medium">{formatType(request.current_type)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Requested Type</p>
                            <p className="font-medium text-blue-600">{formatType(request.requested_type)}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Vendor</p>
                            <p className="font-medium">{request.vendor_name}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Requested</p>
                            <p className="font-medium">{formatDate(request.requested_at)}</p>
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Vendor's Reason</label>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-sm text-gray-700">{request.reason}</p>
                        </div>
                    </div>

                    {/* Documents */}
                    {request.documents && request.documents.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Submitted Documents ({request.documents.length})
                            </label>
                            <div className="space-y-2">
                                {request.documents.map((doc, index) => (
                                    <DocumentViewer
                                        key={index}
                                        file={doc.path}
                                        label={doc.name}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Decision */}
                    <div className="pt-2 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Decision *</label>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setAction('approve')}
                                className={`flex-1 px-4 py-2.5 rounded-lg border transition-colors ${action === 'approve'
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <Check className="h-4 w-4 inline mr-2" />
                                Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => setAction('reject')}
                                className={`flex-1 px-4 py-2.5 rounded-lg border transition-colors ${action === 'reject'
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <XCircle className="h-4 w-4 inline mr-2" />
                                Reject
                            </button>
                        </div>
                    </div>

                    {/* Rejection Reasons - Modern Card Selection */}
                    {action === 'reject' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Rejection Reason <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {REJECTION_REASONS.map((reason) => {
                                        const isSelected = rejectionReason === reason.value;
                                        return (
                                            <button
                                                key={reason.value}
                                                type="button"
                                                onClick={() => {
                                                    setRejectionReason(reason.value);
                                                    setShowErrors(false);
                                                    if (reason.value !== 'other') {
                                                        setRejectionReasonCustom('');
                                                    }
                                                }}
                                                className={`text-left p-3 rounded-lg border-2 transition-all ${isSelected
                                                    ? 'border-red-500 bg-red-50 shadow-sm'
                                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isSelected
                                                            ? 'border-red-500 bg-red-500'
                                                            : 'border-gray-300'
                                                        }`}>
                                                        {isSelected && <Check className="h-3 w-3 text-white" />}
                                                    </div>
                                                    <div>
                                                        <p className={`text-sm font-medium ${isSelected ? 'text-red-700' : 'text-gray-700'
                                                            }`}>
                                                            {reason.label}
                                                        </p>
                                                        <p className={`text-xs mt-0.5 ${isSelected ? 'text-red-600' : 'text-gray-500'
                                                            }`}>
                                                            {reason.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                                {showErrors && !rejectionReason && (
                                    <div className="mt-2 flex items-center text-sm text-red-600">
                                        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                        Please select a rejection reason
                                    </div>
                                )}
                            </div>

                            {/* Additional Details Textarea */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {rejectionReason === 'other' ? 'Please specify the reason *' : 'Additional Details (optional)'}
                                </label>
                                <textarea
                                    value={rejectionReasonCustom}
                                    onChange={(e) => {
                                        setRejectionReasonCustom(e.target.value);
                                        if (rejectionReason === 'other') {
                                            setShowErrors(false);
                                        }
                                    }}
                                    placeholder={rejectionReason === 'other'
                                        ? 'Provide a detailed reason for rejection...'
                                        : 'Add any additional context or notes...'
                                    }
                                    rows={3}
                                    className={`w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 resize-none ${showErrors && rejectionReason === 'other' && !rejectionReasonCustom.trim()
                                        ? 'border-red-300 bg-red-50'
                                        : 'border-gray-300'
                                        }`}
                                />
                                {showErrors && rejectionReason === 'other' && !rejectionReasonCustom.trim() && (
                                    <div className="mt-1 flex items-center text-sm text-red-600">
                                        <AlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                                        Please provide a detailed reason
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Email Preview */}
                    {action === 'reject' && rejectionReason && (
                        <div className="p-3 border rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-500">Email to Vendor:</span>
                            </div>
                            <div className={`p-3 rounded-lg ${rejectionReason === 'other'
                                ? 'bg-gray-50 border border-gray-200'
                                : 'bg-red-50 border border-red-200'
                                }`}>
                                <p className="text-sm whitespace-pre-wrap">
                                    {rejectionReason === 'other'
                                        ? `Your vendor type change request has been rejected.\nReason: ${rejectionReasonCustom || 'No reason provided'}`
                                        : `Your vendor type change request has been rejected.\n${REJECTION_REASONS.find(r => r.value === rejectionReason)?.label}: ${REJECTION_REASONS.find(r => r.value === rejectionReason)?.description}${rejectionReasonCustom ? `\nAdditional details: ${rejectionReasonCustom}` : ''}`
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isProcessing}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            if (action === 'approve') {
                                setShowApproveModal(true);
                            } else {
                                handleRejectClick();
                            }
                        }}
                        disabled={isProcessing || (action === 'reject' && (!rejectionReason || (rejectionReason === 'other' && !rejectionReasonCustom.trim())))}
                        className={`px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2 ${action === 'approve'
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-red-600 hover:bg-red-700'
                            } disabled:opacity-50`}
                    >
                        {isProcessing ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                                Processing...
                            </>
                        ) : (
                            <>
                                {action === 'approve' ? <Check className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                {action === 'approve' ? 'Approve Request' : 'Reject Request'}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Approve Confirmation Modal */}
            {showApproveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApproveModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Approve Type Change?</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to approve this vendor type change request?
                            The vendor will be able to create multiple stores.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowApproveModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApprove}
                                disabled={isProcessing}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                            >
                                {isProcessing ? 'Processing...' : 'Approve'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Confirmation Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectModal(false)} />
                    <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Type Change?</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Are you sure you want to reject this vendor type change request?
                        </p>
                        <p className="text-sm text-red-600 mb-6">
                            <strong>Reason:</strong> {rejectionReason === 'other' ? rejectionReasonCustom : REJECTION_REASONS.find(r => r.value === rejectionReason)?.label}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={isProcessing}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                            >
                                {isProcessing ? 'Processing...' : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewTypeChangeModal;